import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { isProd } from "src/helpers/env.helpers";
import {
  PAUSE_POLLING_WHEN_UNFOCUSED,
  SURVEY_RESULTS_SUMMARY_REFRESH_INTERVAL_MS,
} from "src/constants/polling.constants";
import { useGetSurveyQuery, useGetSurveyStatsQuery, useGetSurveySummaryQuery } from "src/redux/survey/survey.endpoints";
import { SurveyFull, SurveyStats, SurveyStatus, SurveySummaryData } from "src/redux/survey/survey.types";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "src/redux/redux-store";
import { clearSelectedAnswers } from "src/redux/survey/survey-selected-answers.slice";
import { combineQuestionStats } from "src/pages/view-survey/view-survey.helpers";
import type { CombinedQuestionStats } from "src/pages/view-survey/view-survey.types";

const { Complete, Live } = SurveyStatus;

interface UseViewSurveyDataOptions {
  readonly isGeneratingSummary?: boolean;
}

interface UseViewSurveyDataReturn {
  readonly isShare: boolean,
  readonly shareId?: string,
  readonly showSummaries: boolean,
  readonly stats?: SurveyStats,
  readonly survey?: SurveyFull,
  readonly surveyId: string;
  readonly surveyError?: boolean,
  readonly summary?: SurveySummaryData,
  readonly questions: CombinedQuestionStats[],
}

export const useViewSurveyData = (options?: UseViewSurveyDataOptions): UseViewSurveyDataReturn => {
  const dispatch = useDispatch();
  const selectedAnswerIds = useSelector((state: RootState) => {
    return state.selectedAnswers.selectedAnswerIds;
  });
  const refreshConfig = 0;

  const { extSurveyId } = useParams<{ extSurveyId?: string }>();

  const [searchParams] = useSearchParams();
  const shareId = searchParams.get('share-id') ?? '';
  const surveyId = extSurveyId !== '' ? extSurveyId : shareId;
  const isShare = Boolean(shareId);

  useEffect(() => {
    if (surveyId) {
      dispatch(clearSelectedAnswers());
    }
  }, [surveyId, dispatch]);

  const fetchParams = { ext_survey_id: surveyId || '', share: isShare, selected_survey_answers: selectedAnswerIds };
  const [statsRefreshInterval, setStatsRefreshInterval] = useState<number | undefined>(undefined);
  const surveyFetchOptions = {
    skip: !surveyId,
    refetchOnMountOrArgChange: true,
    pollingInterval: statsRefreshInterval,
    ...PAUSE_POLLING_WHEN_UNFOCUSED,
  };
  const { data: survey, isError: surveyError } = useGetSurveyQuery(fetchParams, surveyFetchOptions);
  const { data: stats } = useGetSurveyStatsQuery(fetchParams, surveyFetchOptions);

  const hasFetchedAfterComplete = useRef(false);
  const [summaryRefreshInterval, setSummaryRefreshInterval] = useState<number | undefined>(undefined);
  const showSummaries = survey?.surveyStatus === SurveyStatus.Complete || survey?.surveyStatus === SurveyStatus.Live;
  const { data: summary } = useGetSurveySummaryQuery(fetchParams, {
    pollingInterval: summaryRefreshInterval,
    skip: !showSummaries || !summaryRefreshInterval,
    ...PAUSE_POLLING_WHEN_UNFOCUSED,
  });

  const questions = useMemo(
    () => combineQuestionStats(stats?.questions, survey?.questions, summary?.questions),
    [stats?.questions, survey?.questions, summary?.questions]
  );

  useEffect(() => {
    let blockedByAICompletion = false;
    if (!isProd() && survey && (survey.maxAICompletes !== null || survey.maxCompletes !== 0) && stats) {
      const completed = stats.total_users?.completed ?? stats.totalUsers ?? 0;
      const maxCompletes = survey.maxAICompletes ?? survey.maxCompletes ?? 0;
      blockedByAICompletion = maxCompletes > 0 && completed >= maxCompletes;
    }

    const shouldRefreshAsComplete = survey?.surveyStatus === Complete && !hasFetchedAfterComplete.current;
    const shouldRefresh = Boolean(refreshConfig && (survey?.surveyStatus === SurveyStatus.Live || shouldRefreshAsComplete) && !blockedByAICompletion);
    if (shouldRefresh && shouldRefreshAsComplete) {
      hasFetchedAfterComplete.current = true;
    }
    setStatsRefreshInterval(shouldRefresh ? refreshConfig * 1000 : undefined);
  }, [refreshConfig, survey, stats]);

  useEffect(() => {
    const shouldPoll = showSummaries && (!summary || options?.isGeneratingSummary);
    setSummaryRefreshInterval(shouldPoll ? SURVEY_RESULTS_SUMMARY_REFRESH_INTERVAL_MS : undefined);
  }, [summary, showSummaries, options?.isGeneratingSummary]);

  return {
    isShare,
    shareId: shareId || undefined,
    showSummaries,
    stats,
    survey,
    surveyError,
    summary,
    surveyId: surveyId || '',
    questions,
  };
};
